import React from 'react';
import { 
    X, Mail, Phone, MapPin, ShieldCheck, Lock, Key, Activity, 
    AlertTriangle, BookOpen, Award, Send, MessageSquare, 
    Settings, Shield, ChevronDown, ChevronUp, ExternalLink
  } from 'lucide-react';

const HelpCenterModal = ({ helpCenterModalOpen, setHelpCenterModalOpen, setContactModalOpen }) => { 
    
    const HelpCard = ({ icon, title, description, link, linkText }) => (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-blue-100 dark:bg-blue-900/20 p-2 rounded-full">
              {icon}
            </div>
            <h4 className="font-semibold text-gray-900 dark:text-white">{title}</h4>
          </div>
          <p className="text-gray-700 dark:text-gray-300 text-sm mb-3">{description}</p>
          <a href={link} className="text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium">
            {linkText} →
          </a>
        </div>
      )

      const HelpArticleLink = ({ title, url, views }) => (
        <a href={url} className="group block">
          <div className="flex justify-between items-center py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            <span className="text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400">{title}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{views} views</span>
          </div>
        </a>
      )

      const VideoCard = ({ title, duration, thumbnail, url }) => (
        <a href={url} className="group block">
          <div className="relative aspect-video bg-gray-200 dark:bg-gray-800 rounded-lg overflow-hidden mb-2">
            <img 
              src={thumbnail} 
              alt="" 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            />
            <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
              {duration}
            </div>
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-white p-3 rounded-full shadow-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          <h5 className="text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">{title}</h5>
        </a>
      )
    
    return ( 
    <>  
     {helpCenterModalOpen && (
        <div 
            className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={() => setHelpCenterModalOpen(false)}
        >
            <div 
            className="bg-white dark:bg-gray-900 rounded-xl max-w-5xl w-full p-8 shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
            >
            <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                <h3 className="text-2xl font-bold dark:text-white flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    Help Center
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Find answers, guides, and troubleshooting resources
                </p>
                </div>
                <button
                onClick={() => setHelpCenterModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close help center"
                >
                <X size={24} />
                </button>
            </div>

            <div className="mb-8">
                <div className="relative">
                <input
                    type="text"
                    placeholder="Search help articles, guides, and FAQs..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all"
                />
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 dark:text-gray-500 absolute left-3 top-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
                <HelpCard 
                icon={<BookOpen size={20} className="text-blue-600 dark:text-blue-400" />}
                title="Getting Started"
                description="New user guides and onboarding resources"
                link="/help/getting-started"
                linkText="View guides"
                />
                <HelpCard 
                icon={<Settings size={20} className="text-blue-600 dark:text-blue-400" />}
                title="Configuration"
                description="Setup guides for all platform features"
                link="/help/configuration"
                linkText="See configurations"
                />
                <HelpCard 
                icon={<Shield size={20} className="text-blue-600 dark:text-blue-400" />}
                title="Security Best Practices"
                description="Maximize your protection"
                link="/help/security"
                linkText="Learn more"
                />
            </div>

            <div className="grid md:grid-cols-2 gap-8">
                <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
                    Popular Articles
                </h4>
                <div className="space-y-3">
                    <HelpArticleLink 
                    title="How to set up multi-factor authentication"
                    url="/help/mfa-setup"
                    views="12.4k"
                    />
                    <HelpArticleLink 
                    title="Troubleshooting common installation issues"
                    url="/help/install-troubleshooting"
                    views="8.7k"
                    />
                    <HelpArticleLink 
                    title="Configuring automated threat responses"
                    url="/help/automated-responses"
                    views="6.2k"
                    />
                    <HelpArticleLink 
                    title="Integrating with your SIEM solution"
                    url="/help/siem-integration"
                    views="5.9k"
                    />
                    <HelpArticleLink 
                    title="Understanding security alerts"
                    url="/help/alert-types"
                    views="5.3k"
                    />
                </div>
                </div>

                <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
                    Video Tutorials
                </h4>
                <div className="grid gap-4">
                    <VideoCard 
                    title="Dashboard Overview"
                    duration="4:12"
                    thumbnail="/thumbnails/dashboard.jpg"
                    url="/tutorials/dashboard"
                    />
                    <VideoCard 
                    title="Creating Custom Security Policies"
                    duration="7:35"
                    thumbnail="/thumbnails/policies.jpg"
                    url="/tutorials/policies"
                    />
                    <VideoCard 
                    title="Incident Response Walkthrough"
                    duration="10:48"
                    thumbnail="/thumbnails/incident.jpg"
                    url="/tutorials/incident"
                    />
                </div>
                </div>
            </div>

            <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Need more help?</h4>
                <div className="grid sm:grid-cols-3 gap-4">
                <button
                    onClick={() =>{ setContactModalOpen(true); setHelpCenterModalOpen(false); }} 
                    className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2"
                >
                    <MessageSquare size={16} />
                    Live Chat
                </button>
                <a
                    href="mailto:support@0x10layerff.com"
                    className="px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                >
                    <Mail size={16} />
                    Email Support
                </a>
                <a
                    href="tel:+15551234567"
                    className="px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                >
                    <Phone size={16} />
                    Call Support
                </a>
                </div>
            </div>
            </div>
        </div>
        )}
    </>
  );
};

export default HelpCenterModal;